import { useEffect, useState } from 'react';
import { api, OpsEvent, OpsEventSummary } from '../../services/api';

/**
 * OpsEvents — 運用イベント（「静かな失敗」の可視化・Admin）
 *
 * 2026-08-10、閉室時のR2書き出しが全部屋で失敗し続けていたのに warn ログだけで誰も気づけなかった。
 * catch → console.warn('... non-fatal') の形は約20箇所あるが、ここに出るのは
 * **失敗するとユーザーの成果物が永久に失われる4系統だけ**（docs/OPS_NOTES.md §2）。
 *
 * 【事実性①】件数は実データのみ。
 *   - 取得できなかった（null）ときは「取得できませんでした」と出す
 *   - 0件のときは「直近7日の記録はありません」と事実を出す（0件と取得失敗を混同しない）
 *   - 種別は生の英語キーではなく日本語ラベルで出す（生キーも小さく併記して調査に使えるようにする）
 *
 * UI型は PromptManagement.tsx を踏襲（loading / error / empty の明示・44pxタップ領域・aria-pressed）。
 */

/** 記録される種別（workers/anokoe/src/lib/opsEvents.ts の OPS_EVENT と1:1） */
const KINDS: { key: string; label: string; lost: string }[] = [
    { key: 'credits_insert_failed', label: '三役クレジットの記録に失敗', lost: '誰が参加したかの記録が残らない' },
    { key: 'pipe01_writeback_failed', label: '街区への作品化の書き戻しに失敗', lost: '街区側に「作品化済み」が立たない' },
    { key: 'reward_enqueue_failed', label: '報酬キューへの登録に失敗', lost: '受け取るはずの報酬が届かない' },
    { key: 'canvas_export_failed', label: '共有キャンバスのR2書き出しに失敗', lost: '筆致リプレイの元データが残らない' },
    { key: 'canvas_participant_failed', label: '共有キャンバスの参加者記録に失敗', lost: '入退室がクレジットに反映されない' },
    { key: 'stage_participant_failed', label: '稽古場の参加者記録に失敗', lost: '入退室がクレジットに反映されない' },
];

const labelOf = (kind: string) => KINDS.find(k => k.key === kind)?.label ?? kind;

const fmtAt = (unixSec: number) =>
    new Date(unixSec * 1000).toLocaleString('ja-JP', {
        month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });

export const OpsEvents = () => {
    const [kind, setKind] = useState<string>('');   // '' = すべて
    const [summary, setSummary] = useState<OpsEventSummary | null>(null);
    const [events, setEvents] = useState<OpsEvent[] | null>(null);
    const [loading, setLoading] = useState(true);

    const load = async (k: string) => {
        setLoading(true);
        const [s, e] = await Promise.all([api.adminOpsEventsSummary(), api.adminListOpsEvents(k || undefined, 100)]);
        setSummary(s);
        setEvents(e);
        setLoading(false);
    };
    useEffect(() => { load(kind); }, [kind]);

    // 集計に現れた種別のうち、KINDS に無いもの（後から足された種別）も落とさず出す
    const summaryRows = summary
        ? summary.items.map(i => ({ ...i, label: labelOf(i.kind) }))
        : [];
    const summaryTotal = summaryRows.reduce((n, r) => n + r.count, 0);

    return (
        <div className="space-y-6">
            {/* この画面の意味（何が出て、何が出ないか） */}
            <div className="bg-cream-2 border-2 border-ink/15 rounded-pop-md p-4">
                <h2 className="font-display font-bold text-ink text-lg">運用イベント</h2>
                <p className="text-sm text-ink-2 mt-1">
                    失敗しても画面にはエラーが出ないまま握りつぶされる処理のうち、
                    <strong className="text-ink">失敗するとユーザーの成果物が永久に失われる4系統</strong>だけを記録しています。
                    ログにも同じ内容が残ります（30日、最大5,000件で自動的に古いものから消えます）。
                </p>
            </div>

            {/* 直近7日の集計（急増に気づくため） */}
            <section className="bg-surface border-2 border-ink rounded-pop-md p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <h3 className="font-display font-bold text-ink">直近7日の記録</h3>
                    <button
                        onClick={() => load(kind)}
                        disabled={loading}
                        className="btn btn-ghost btn-sm min-h-[44px]"
                    >
                        {loading ? '読み込んでいます…' : '最新に読み直す'}
                    </button>
                </div>

                {/* 【事実性①】間引いている以上、この数字は「発生回数」ではなく「記録された回数」。
                    実態より少ない数字を「発生件数」と呼ばない */}
                <p className="text-xs text-ink-2 mb-3">
                    同じ種別は60秒に1件だけ記録されます（実際の発生回数はこれより多い場合があります）。
                </p>

                {loading ? (
                    <div className="h-6 w-48 bg-cream-2 rounded motion-safe:animate-pulse" aria-label="読み込み中" />
                ) : !summary ? (
                    <p className="text-sm text-ink-2" role="status">
                        集計を取得できませんでした。件数は不明です（0件という意味ではありません）。
                    </p>
                ) : summaryRows.length === 0 ? (
                    <p className="text-sm text-ink-2" role="status">直近7日の記録はありません。</p>
                ) : (
                    <>
                        <ul className="grid gap-2 sm:grid-cols-2 list-none p-0 m-0">
                            {summaryRows.map(r => (
                                <li key={r.kind} className="flex items-center justify-between gap-3 bg-cream-2 border-2 border-ink/15 rounded-pop-sm px-3 py-2">
                                    <span className="text-sm text-ink font-bold">{r.label}</span>
                                    <span className="text-lg font-display font-bold text-ink tabular-nums">{r.count}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-xs text-ink-2 mt-2 tabular-nums">記録された合計 {summaryTotal} 件</p>
                    </>
                )}
            </section>

            {/* 種別の絞り込み */}
            <div className="flex flex-wrap gap-2">
                <button
                    onClick={() => setKind('')}
                    aria-pressed={kind === ''}
                    className={`min-h-[44px] px-4 py-2 rounded-pop-sm border-2 font-bold text-sm transition-transform ${kind === '' ? 'bg-primary-soft text-ink border-ink' : 'bg-surface text-ink-2 border-ink/20 hover:-translate-y-0.5'}`}
                >
                    すべて
                </button>
                {KINDS.map(k => (
                    <button
                        key={k.key}
                        onClick={() => setKind(k.key)}
                        aria-pressed={kind === k.key}
                        // title は付けない（支援技術がボタン名を title で読み替え、見た目の文言と食い違う）。
                        // 「失われるもの」は各行に表示している
                        className={`min-h-[44px] px-4 py-2 rounded-pop-sm border-2 font-bold text-sm transition-transform ${kind === k.key ? 'bg-primary-soft text-ink border-ink' : 'bg-surface text-ink-2 border-ink/20 hover:-translate-y-0.5'}`}
                    >
                        {k.label}
                    </button>
                ))}
            </div>

            {/* 一覧（新しい順・最大100件） */}
            <section className="space-y-2">
                <h3 className="text-xs font-bold text-ink-2">記録（新しい順・最大100件）</h3>
                {loading ? (
                    <div className="space-y-2">
                        {[0, 1, 2].map(i => <div key={i} className="h-16 bg-cream-2 rounded-pop-sm motion-safe:animate-pulse" />)}
                    </div>
                ) : !events ? (
                    <p className="text-sm text-ink-2" role="status">
                        一覧を取得できませんでした。件数は不明です（0件という意味ではありません）。
                    </p>
                ) : events.length === 0 ? (
                    <p className="text-sm text-ink-2" role="status">
                        {kind ? 'この種別の記録はありません。' : 'まだ記録はありません。'}
                    </p>
                ) : (
                    <ul className="space-y-2 list-none p-0 m-0">
                        {events.map(ev => {
                            const meta = KINDS.find(k => k.key === ev.kind);
                            return (
                                <li key={ev.id} className="bg-surface border-2 border-ink/20 rounded-pop-sm p-3">
                                    <div className="flex items-baseline gap-3 flex-wrap">
                                        <time className="text-[11px] text-ink-2 tabular-nums" dateTime={new Date(ev.at * 1000).toISOString()}>
                                            {fmtAt(ev.at)}
                                        </time>
                                        <span className="text-sm font-bold text-ink">{labelOf(ev.kind)}</span>
                                        <code className="text-[10px] text-ink-2 bg-cream-2 rounded px-1.5 py-0.5">{ev.kind}</code>
                                    </div>
                                    {meta && <p className="text-xs text-ink-2 mt-1">失われるもの: {meta.lost}</p>}
                                    {ev.ref && (
                                        <p className="text-xs text-ink-2 mt-1 break-all">
                                            関連ID: <code className="bg-cream-2 rounded px-1.5 py-0.5">{ev.ref}</code>
                                        </p>
                                    )}
                                    {ev.detail && (
                                        <p className="text-xs text-ink-2 mt-1 break-words whitespace-pre-wrap">{ev.detail}</p>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>
        </div>
    );
};
